import React, { Component } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import styled from "styled-components";
import shortid from "shortid";

import BaseArrow from "../../../../lib/components/Arrow";
import Tooltip, { withTooltip } from "../../../../lib/components/Tooltip";
import bindActionCreators from "../../../../lib/utils/bindActionCreators";
import { toggleOrder } from "../../../../lib/utils";

import { PATH, HEADER } from "../../../project-information/constants";

import { getTable, getSort } from "../selectors";
import { setSort } from "../actions";

import withRedirect from "../hocs/withRedirect";
import ScrollWrapper from "../contexts/ScrollWrapper";
import moment from "moment";

/**
 * @typedef {import('../model').PPSReport} PPSReport
 */

const Root = styled.table`
  width: 100%;
  height: 100%;
  display: block;
`;

const THead = styled.thead`
  height: 50px;

  display: flex;
  align-items: center;

  background-color: #404040;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.08);
`;

export const TBody = styled.tbody`
  max-height: calc(100% - 50px);
  padding-bottom: 24px;

  display: block;
  background-color: #fff;

  overflow-x: hidden;
  overflow-y: scroll;

  > * {
    border-bottom: 1px solid #ddd;
    cursor: pointer;
  }

  > :hover {
    background-color: #ededed;
  }
`;

export const Tr = styled.tr`
  width: 100%;
  height: 45px;

  display: grid;
  grid-template-columns:
    140px
    minmax(140px, 1fr)
    125px
    55px
    170px
    80px
    100px;
  grid-template-rows: 100%;
  align-items: center;

  > * {
    border-left: 1px solid #dee2e6;
  }

  > :first-child {
    padding-left: 16px;
    border-left: none;
  }

  > :last-child {
    padding-right: 16px;
  }
`;

const Th = styled.th`
  width: 100%;
  height: 100%;
  padding: 0 12px;

  display: flex;
  align-items: center;

  color: #fff;
  font-family: "NotoSans" !important;
  font-size: 14px;

  cursor: pointer;
`;

const Td = styled.td`
  width: 100%;
  height: 100%;
  padding: 0 12px;

  display: flex;
  align-items: center;

  color: #333333;
  font-family: "NotoSans";
  font-size: 14px;
  line-height: 100%;

  ${withTooltip}

  ${Tooltip} {
    top: 80%;
  }

  &:hover ${Tooltip} {
    transition-delay: 1s;
  }
`;

const Ellipsis = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Arrow = styled(BaseArrow)`
  pointer-events: none;
`;

/**
 * @typedef {object} Props
 * @property {{
 *   id: string,
 *   code: string,
 *   name: string,
 *   revenue: string,
 *   pm: string[],
 *   lob: string,
 *   client: string,
 * }[]} data
 * @property {(payload: { to: string }) => void} redirect
 * @property {object} sort
 * @property {typeof setSort} setSort
 */

/** @extends {Component<Props>} */
export class Table extends Component {
  /** @type {Props} */
  static defaultProps = {
    data: [],
    sort: {},
    redirect: () => {},
    setSort: () => {},
  };

  /** @type {(id: string) => () => void} */
  toInformation = (id) => () =>
    this.props.redirect({
      to: `${PATH}/${id}`,
      header: HEADER,
    });

  /** @type {(key: string) => () => void} */
  handleColumnClick = (key) => () => {
    const { setSort, sort } = this.props;

    if (key === sort.field) {
      setSort("order", toggleOrder(sort.order));
    } else {
      setSort("field", key);
    }
  };

  /**
   * @param {string} field
   */
  renderArrow = (field) => {
    const { sort } = this.props;
    const ORDER_DIRECTION = { asc: "up", desc: "down" };
    return sort.field === field ? (
      <Arrow direction={ORDER_DIRECTION[sort.order]} />
    ) : null;
  };

  render = () => {
    const { data } = this.props;
    return (
      <Root>
        <THead>
          <Tr style={{ height: "100%" }}>
            <Th onClick={this.handleColumnClick("code")}>
              Proj. Code
              {this.renderArrow("code")}
            </Th>
            <Th onClick={this.handleColumnClick("name")}>
              Project Name
              {this.renderArrow("name")}
            </Th>
            <Th onClick={this.handleColumnClick("revenue")}>
              Revenue
              {this.renderArrow("revenue")}
            </Th>
            <Th onClick={this.handleColumnClick("lob")}>
              LOB
              {this.renderArrow("lob")}
            </Th>
            <Th onClick={this.handleColumnClick("key_project_manager")}>
              Key PM
              {this.renderArrow("key_project_manager")}
            </Th>
            <Th onClick={this.handleColumnClick("gross_profit_approved_ratio")}>
              Approved
              <br />
              GP%
              {this.renderArrow("gross_profit_approved_ratio")}
            </Th>
            <Th onClick={this.handleColumnClick("latest_approve_date")}>
              LastApprove
              <br />
              Date
              {this.renderArrow("latest_approve_date")}
            </Th>
          </Tr>
        </THead>
        <ScrollWrapper.Consumer>
          {(Wrapper) => (
            <Wrapper>
              <TBody>
                {data &&
                  data.map((row) => (
                    <Tr
                      onClick={this.toInformation(row.id)}
                      key={shortid.generate()}
                    >
                      <Td style={{ fontFamily: "NotoSans" }}>
                        <Ellipsis>{row.code}</Ellipsis>
                        <Tooltip>{row.code}</Tooltip>
                      </Td>
                      <Td>
                        <Ellipsis>{row.name}</Ellipsis>
                        <Tooltip>{row.name}</Tooltip>
                      </Td>
                      <Td
                        style={{
                          justifyContent: "flex-end",
                          fontFamily: "NotoSans",
                          textAlign: "right",
                        }}
                      >
                        {row.revenue}
                      </Td>
                      <Td>{row.lob}</Td>
                      <Td>{row.key_project_manager}</Td>
                      <Td>
                        {row.gross_profit_approved_ratio !== "-"
                          ? parseFloat(row.gross_profit_approved_ratio).toFixed(
                              2
                            )
                          : "-"}
                      </Td>
                      <Td>
                        {row.latest_approve_date
                          ? moment(row.latest_approve_date).format("DD/MM/YYYY")
                          : "-"}
                      </Td>
                    </Tr>
                  ))}
              </TBody>
            </Wrapper>
          )}
        </ScrollWrapper.Consumer>
      </Root>
    );
  };
}

const mapStateToProps = createStructuredSelector({
  data: getTable,
  sort: getSort,
});

const mapDispatchToProps = bindActionCreators({
  setSort,
});

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  withRedirect
)(Table);



// WEBPACK FOOTER //
// ./src/pages/report/project-profit-sheet/components/Table.jsx